import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Crown, Send, Lock, MessageSquare } from "lucide-react";

export default function Coaching() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const active = status?.active || user?.role === "admin";

  useEffect(() => {
    api.get("/coaching/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ active: false }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get("/coaching/messages").then(({ data }) => setMessages(data)).catch(() => {});
  }, [active]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    try {
      const { data } = await api.post("/coaching/messages", { text });
      setMessages((m) => [...m, data]);
      setInput("");
    } catch (e) {}
  };

  if (loading) return <AppShell title="1-on-1 Coaching"><Skeleton className="h-64 w-full rounded-2xl bg-card" /></AppShell>;

  if (!active) {
    return (
      <AppShell title="1-on-1 Coaching">
        <div className="rd-fade-in">
          <Card className="rounded-2xl bg-card border-border p-6 text-center" data-testid="coaching-upsell">
            <div className="h-14 w-14 rounded-2xl bg-[#ccff0c]/15 flex items-center justify-center mx-auto mb-4">
              <Crown className="h-7 w-7 text-[#ccff0c]" />
            </div>
            <h2 className="font-display text-2xl font-bold">Unlock 1-on-1 Coaching</h2>
            <p className="text-muted-foreground text-sm mt-2 mb-5">
              Message a Real Deal coach directly for personalized programming, form checks, and accountability.
              Available on the Coaching plan ($100/mo or $900/yr).
            </p>
            <Button data-testid="coaching-subscribe-cta" onClick={() => navigate("/subscribe")} className="bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold">
              <Lock className="h-4 w-4 mr-2" /> View coaching plans
            </Button>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="1-on-1 Coaching" hideNav>
      <div className="flex flex-col" style={{ height: "calc(100vh - 170px)" }}>
        <div data-testid="coaching-thread" className="flex-1 overflow-y-auto space-y-3 pb-4 rd-scrollbar">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center">
              <MessageSquare className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-muted-foreground text-sm">Say hi to your coach! Ask about your program, nutrition, or goals.</p>
            </div>
          ) : messages.map((m) => {
            const mine = m.sender === "user";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div data-testid={`coaching-msg-${m.id}`} className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${mine ? "bg-[#ccff0c] text-black" : "bg-secondary text-foreground border border-border"}`}>
                  {!mine && <p className="text-[10px] font-bold text-[#ccff0c] mb-0.5 flex items-center gap-1"><Crown className="h-3 w-3" /> COACH</p>}
                  <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                  <p className={`text-[10px] mt-1 ${mine ? "text-black/50" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="flex items-end gap-2 pt-2 border-t border-border">
          <Input data-testid="coaching-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Message your coach…" className="bg-card border-border h-11" />
          <Button data-testid="coaching-send" onClick={send} disabled={!input.trim()} className="h-11 w-11 p-0 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] flex-shrink-0"><Send className="h-5 w-5" /></Button>
        </div>
      </div>
    </AppShell>
  );
}
